/** Frontend API wrapper for workspace-scoped CIS returns.
 *
 * Mirrors CashFlowPlansService: one static method per route, getAuthHeaders()
 * on every call, all errors surfaced via thrown Error.
 */

import { API_BASE, getAuthHeaders } from '../utils/supabase/client';
import type { CISReturn, CISReturnStatus } from '../types/accounting';
import { readError } from './_http';
import { createWorkspaceListCache } from './_listCache';

// Stale-while-revalidate cache — keyed by workspaceId, populated on unfiltered list fetches.
const listCache = createWorkspaceListCache<CISReturn>();

export class CISService {
  static getCached(workspaceId: string): CISReturn[] | undefined {
    return listCache.get(workspaceId);
  }

  static async list(params: {
    workspaceId: string;
    status?: CISReturnStatus;
  }): Promise<CISReturn[]> {
    const p = new URLSearchParams({ workspaceId: params.workspaceId });
    if (params.status) p.set('status', params.status);
    const res = await fetch(`${API_BASE}/cis-returns?${p}`, {
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
    const result: CISReturn[] = await res.json();
    // Only cache the unfiltered result so getCached always reflects the full set of returns.
    if (!params.status) listCache.set(params.workspaceId, result);
    return result;
  }

  static async getById(id: string): Promise<CISReturn> {
    const res = await fetch(`${API_BASE}/cis-returns/${encodeURIComponent(id)}`, {
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  static async create(data: Partial<CISReturn>): Promise<CISReturn> {
    const res = await fetch(`${API_BASE}/cis-returns`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  static async update(id: string, data: Partial<CISReturn>): Promise<CISReturn> {
    const res = await fetch(`${API_BASE}/cis-returns/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }

  static async remove(id: string): Promise<void> {
    const res = await fetch(`${API_BASE}/cis-returns/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
  }

  static async submit(id: string): Promise<CISReturn> {
    const res = await fetch(`${API_BASE}/cis-returns/${encodeURIComponent(id)}/submit`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
  }
}
